import type { Schemas } from '@argzombie/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { unwrap, useApi } from '../lib/api';

type Building = Schemas['BuildingView'];

const categoryColor: Record<string, string> = {
  medical: 'var(--color-benday)',
  food: 'var(--color-caption)',
  police: 'var(--color-bruise)',
  hardware: 'var(--color-ink)',
  supply_cache: 'var(--color-formaldehyde)',
};

/** Plots footprints around a point (north up, meters from the center) so bad category mappings stand out. */
function FootprintMap({
  buildings,
  lat,
  lon,
  radius,
  selected,
  onSelect,
}: {
  buildings: Building[];
  lat: number;
  lon: number;
  radius: number;
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  const mLat = 111_320;
  const mLon = 111_320 * Math.cos((lat * Math.PI) / 180);
  const toXY = (p: { lat: number; lon: number }) => [(p.lon - lon) * mLon, -(p.lat - lat) * mLat];
  return (
    <svg
      viewBox={`${-radius} ${-radius} ${radius * 2} ${radius * 2}`}
      className="aspect-square w-full border-2 border-ink bg-newsprint"
    >
      <circle cx={0} cy={0} r={radius * 0.01 + 3} fill="var(--color-blam)" />
      {buildings.map((b) => (
        <polygon
          key={b.id}
          points={b.footprint.map((p) => toXY(p).join(',')).join(' ')}
          fill={categoryColor[b.category] ?? 'gray'}
          fillOpacity={b.id === selected ? 1 : 0.7}
          stroke="var(--color-ink)"
          strokeWidth={b.id === selected ? 3 : 0.5}
          onClick={() => onSelect(b.id)}
          className="cursor-pointer"
        >
          <title>{`${b.name ?? 'unnamed'} (${b.category})`}</title>
        </polygon>
      ))}
    </svg>
  );
}

export function BuildingsPage() {
  const { client, session } = useApi();
  const qc = useQueryClient();
  const [center, setCenter] = useState({ lat: 45.5202, lon: -122.6742, radius: 600 });
  const [form, setForm] = useState(center);
  const [selected, setSelected] = useState<number | null>(null);

  const buildings = useQuery({
    queryKey: ['admin-buildings', center],
    queryFn: async () =>
      unwrap(
        await client.GET('/admin/buildings', {
          params: { query: { lat: center.lat, lon: center.lon, radiusM: center.radius } },
        }),
      ),
  });

  const importBox = useMutation({
    mutationFn: async () => {
      const dLat = center.radius / 111_320;
      const dLon = center.radius / (111_320 * Math.cos((center.lat * Math.PI) / 180));
      return unwrap(
        await client.POST('/admin/buildings/import', {
          body: {
            south: center.lat - dLat,
            west: center.lon - dLon,
            north: center.lat + dLat,
            east: center.lon + dLon,
          },
        }),
      );
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-buildings'] }),
  });

  const counts = new Map<string, number>();
  buildings.data?.forEach((b) => counts.set(b.category, (counts.get(b.category) ?? 0) + 1));
  const pick = buildings.data?.find((b) => b.id === selected);

  return (
    <>
      <PageHeader
        title="Buildings"
        description="Imported buildings around a point, colored by category. Click one to inspect it."
      >
        {session.isAdmin && (
          <button
            type="button"
            onClick={() => importBox.mutate()}
            disabled={importBox.isPending}
            className="rounded border-2 border-ink bg-caption px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            {importBox.isPending ? 'Importing…' : 'Import this area from OSM'}
          </button>
        )}
      </PageHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setCenter(form);
        }}
        className="mb-4 flex flex-wrap gap-2 text-sm"
      >
        {(['lat', 'lon', 'radius'] as const).map((k) => (
          <label key={k} className="flex items-center gap-1">
            {k}
            <input
              type="number"
              step="any"
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
              className="w-36 rounded border-2 border-ink px-2 py-1 font-mono"
            />
          </label>
        ))}
        <button
          type="submit"
          className="rounded border-2 border-ink bg-benday px-3 py-1 font-semibold"
        >
          Show
        </button>
      </form>
      {importBox.data && (
        <p className="mb-2 text-sm">
          Imported {importBox.data.inserted} new of {importBox.data.fetched} lootable buildings
          found.
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <FootprintMap
          buildings={buildings.data ?? []}
          lat={center.lat}
          lon={center.lon}
          radius={center.radius}
          selected={selected}
          onSelect={setSelected}
        />
        <div className="space-y-4 text-sm">
          <section className="rounded border-2 border-ink bg-white p-3">
            <h2 className="font-semibold">{buildings.data?.length ?? 0} buildings</h2>
            <ul>
              {[...counts.entries()].map(([c, n]) => (
                <li key={c} className="flex items-center gap-2">
                  <span
                    className="inline-block size-3 border border-ink"
                    style={{ background: categoryColor[c] }}
                  />
                  {c}: {n}
                </li>
              ))}
            </ul>
          </section>
          {pick && (
            <section className="rounded border-2 border-ink bg-white p-3">
              <h2 className="font-semibold">{pick.name ?? 'Unnamed'}</h2>
              <p>
                #{pick.id} · {pick.category} · difficulty {pick.difficulty}
              </p>
              <p>
                {pick.areaM2} m² · {pick.distanceM} m from center
              </p>
              <p>Likely loot: {pick.likelyLoot.join(', ')}</p>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
